<template>
  <div class="app-container asset-overview" v-loading="loading">
    <div class="page-head">
      <div>
        <h3>资产总览</h3>
        <span>统计当前权限范围内的资产</span>
      </div>
      <el-button icon="el-icon-refresh" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="14" class="metric-grid">
      <el-col v-for="item in metricCards" :key="item.key" :xs="12" :sm="8" :lg="3">
        <div class="metric-card" :class="item.tone">
          <div class="metric-label">{{ item.label }}</div>
          <div class="metric-value">{{ number(overview[item.key]) }}</div>
          <div class="metric-note">{{ item.note }}</div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="14" class="chart-row">
      <el-col :xs="24" :lg="10">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-title">资产状态分布</div>
          <div ref="statusChart" class="chart"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="14">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="chart-title">管理归属与账簿属性</div>
          <div ref="structureChart" class="chart"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="14" class="chart-row">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never" class="chart-card large">
          <div slot="header" class="chart-title">资产大类构成及使用状态</div>
          <div ref="categoryChart" class="chart large-chart"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never" class="chart-card large">
          <div slot="header" class="chart-title">部门持有资产排行</div>
          <div ref="departmentChart" class="chart large-chart"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="chart-card trend-card">
      <div slot="header" class="chart-title">资产购置趋势</div>
      <div ref="trendChart" class="chart trend-chart"></div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getAssetOverview } from '@/api/crm/asset'

export default {
  name: 'CrmAssetOverview',
  data() {
    return {
      loading: false,
      overview: {},
      charts: [],
      metricCards: [
        { key: 'totalCount', label: '资产总数', note: '含已报废资产', tone: 'blue' },
        { key: 'inUseCount', label: '在用', note: '当前有人保管', tone: 'green' },
        { key: 'idleCount', label: '闲置', note: '可继续领用', tone: 'gray' },
        { key: 'assignedCount', label: '已分配', note: '未报废且有保管人', tone: 'cyan' },
        { key: 'pendingOperationCount', label: '流转处理中', note: '领用、调拨或归还尚未完成', tone: 'orange' },
        { key: 'repairCount', label: '维修中', note: '正在维修处理', tone: 'orange' },
        { key: 'pendingScrapCount', label: '待报废', note: '审批或处置中', tone: 'red' },
        { key: 'scrappedCount', label: '已报废', note: '生命周期已结束', tone: 'dark' }
      ]
    }
  },
  mounted() {
    window.addEventListener('resize', this.resizeCharts)
    this.loadData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
  },
  methods: {
    loadData() {
      this.loading = true
      getAssetOverview().then(res => {
        this.overview = res.data || {}
        this.$nextTick(this.renderCharts)
      }).finally(() => { this.loading = false })
    },
    number(value) { return Number(value || 0).toLocaleString('zh-CN') },
    value(row, key) {
      if (!row) return 0
      return row[key] !== undefined ? row[key] : row[key.toLowerCase()]
    },
    initChart(refName) {
      const dom = this.$refs[refName]
      if (!dom) return null
      const chart = echarts.getInstanceByDom(dom) || echarts.init(dom)
      this.charts.push(chart)
      return chart
    },
    renderCharts() {
      this.disposeCharts()
      this.renderStatusChart()
      this.renderStructureChart()
      this.renderCategoryChart()
      this.renderDepartmentChart()
      this.renderTrendChart()
    },
    renderStatusChart() {
      const chart = this.initChart('statusChart')
      if (!chart) return
      const data = [
        ['闲置', 'idleCount'], ['在用', 'inUseCount'], ['维修', 'repairCount'],
        ['丢失', 'lostCount'], ['待报废', 'pendingScrapCount'], ['已报废', 'scrappedCount']
      ].map(item => ({ name: item[0], value: Number(this.overview[item[1]] || 0) }))
      chart.setOption({
        color: ['#94a3b8', '#22c55e', '#f59e0b', '#ef4444', '#f97316', '#64748b'],
        tooltip: { trigger: 'item', formatter: '{b}：{c}（{d}%）' },
        legend: { bottom: 2, itemWidth: 10, itemHeight: 10 },
        series: [{ type: 'pie', radius: ['46%', '70%'], center: ['50%', '44%'],
          label: { formatter: '{b}\n{c}' }, itemStyle: { borderColor: '#fff', borderWidth: 2 }, data }]
      })
    },
    renderStructureChart() {
      const chart = this.initChart('structureChart')
      if (!chart) return
      chart.setOption({
        color: ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6'],
        tooltip: { trigger: 'item', formatter: '{b}：{c}（{d}%）' },
        legend: { bottom: 2, itemWidth: 10, itemHeight: 10 },
        title: [
          { text: '管理归属', left: '25%', top: '42%', textAlign: 'center', textStyle: { fontSize: 14, color: '#64748b' } },
          { text: '账簿属性', left: '75%', top: '42%', textAlign: 'center', textStyle: { fontSize: 14, color: '#64748b' } }
        ],
        series: [
          { type: 'pie', radius: ['40%', '64%'], center: ['25%', '43%'], label: { formatter: '{b}\n{c}' },
            data: [{ name: '人事行政', value: Number(this.overview.hrCount || 0) }, { name: '仓库', value: Number(this.overview.warehouseCount || 0) }] },
          { type: 'pie', radius: ['40%', '64%'], center: ['75%', '43%'], label: { formatter: '{b}\n{c}' },
            data: [{ name: '账内', value: Number(this.overview.onBookCount || 0) }, { name: '账外', value: Number(this.overview.offBookCount || 0) }] }
        ]
      })
    },
    renderCategoryChart() {
      const chart = this.initChart('categoryChart')
      if (!chart) return
      const rows = (this.overview.categoryDistribution || []).slice().reverse()
      chart.setOption({
        color: ['#22c55e', '#94a3b8', '#f59e0b'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { top: 0 },
        grid: { left: 120, right: 24, top: 38, bottom: 20 },
        xAxis: { type: 'value', minInterval: 1 },
        yAxis: { type: 'category', data: rows.map(row => this.value(row, 'categoryName')) },
        series: [
          { name: '在用', type: 'bar', stack: 'asset', data: rows.map(row => Number(this.value(row, 'inUseCount') || 0)) },
          { name: '闲置', type: 'bar', stack: 'asset', data: rows.map(row => Number(this.value(row, 'idleCount') || 0)) },
          { name: '其他状态', type: 'bar', stack: 'asset', data: rows.map(row => Math.max(0,
            Number(this.value(row, 'assetCount') || 0) - Number(this.value(row, 'inUseCount') || 0) - Number(this.value(row, 'idleCount') || 0))) }
        ]
      })
    },
    renderDepartmentChart() {
      const chart = this.initChart('departmentChart')
      if (!chart) return
      const rows = (this.overview.departmentDistribution || []).slice().reverse()
      chart.setOption({
        color: ['#3b82f6'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 120, right: 35, top: 18, bottom: 20 },
        xAxis: { type: 'value', minInterval: 1 },
        yAxis: { type: 'category', data: rows.map(row => this.value(row, 'departmentName')) },
        series: [{ type: 'bar', barMaxWidth: 24, label: { show: true, position: 'right' },
          data: rows.map(row => Number(this.value(row, 'assetCount') || 0)) }]
      })
    },
    renderTrendChart() {
      const chart = this.initChart('trendChart')
      if (!chart) return
      const rows = this.overview.purchaseTrend || []
      chart.setOption({
        color: ['#3b82f6', '#10b981'],
        tooltip: { trigger: 'axis' },
        legend: { top: 0 },
        grid: { left: 55, right: 65, top: 42, bottom: 35 },
        xAxis: { type: 'category', data: rows.map(row => this.value(row, 'purchaseYear')) },
        yAxis: [
          { type: 'value', name: '数量', minInterval: 1 },
          { type: 'value', name: '金额（万元）' }
        ],
        series: [
          { name: '购置数量', type: 'bar', barMaxWidth: 36, data: rows.map(row => Number(this.value(row, 'assetCount') || 0)) },
          { name: '购置金额', type: 'line', yAxisIndex: 1, smooth: true,
            data: rows.map(row => (Number(this.value(row, 'purchaseAmount') || 0) / 10000).toFixed(2)) }
        ]
      })
    },
    resizeCharts() { this.charts.forEach(chart => chart && chart.resize()) },
    disposeCharts() {
      this.charts.forEach(chart => chart && !chart.isDisposed() && chart.dispose())
      this.charts = []
    }
  }
}
</script>

<style scoped>
.page-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.page-head h3 { margin:0 0 6px; font-size:20px; }.page-head span { color:#909399; font-size:13px; }
.metric-grid { margin-bottom:2px; }.metric-card { min-height:112px; margin-bottom:14px; padding:16px 17px; border-radius:8px; background:#f8fafc; border-top:3px solid #3b82f6; box-sizing:border-box; }
.metric-card.green { border-color:#22c55e; }.metric-card.gray { border-color:#94a3b8; }.metric-card.cyan { border-color:#06b6d4; }.metric-card.orange { border-color:#f59e0b; }.metric-card.red { border-color:#ef4444; }.metric-card.dark { border-color:#475569; }
.metric-label { color:#64748b; font-size:13px; }.metric-value { margin:8px 0 4px; color:#172033; font-size:28px; font-weight:700; }.metric-note { color:#a0aec0; font-size:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.chart-row { margin-top:14px; }.chart-card { border-color:#e8edf5; border-radius:8px; }.chart-card /deep/ .el-card__header { padding:14px 18px; border-bottom-color:#edf1f7; }.chart-card /deep/ .el-card__body { padding:8px 12px 12px; }
.chart-title { color:#28354d; font-size:15px; font-weight:600; }.chart { width:100%; height:310px; }.large-chart { height:380px; }.trend-card { margin-top:14px; }.trend-chart { height:330px; }
@media (max-width: 1200px) { .chart-row .el-col + .el-col { margin-top:14px; } }
</style>
